"use client";

/**
 * agent 控制台（平台首页 · 明细行第三列）。
 *
 * 三件事一张卡：
 *   · **开关**（默认开）—— 关掉 = agent 停用：门户侧不再推进编排任务，
 *     飞书桥也不再往事件流里灌指令（`POST /api/agent-console/events` 直接拒收）。
 *   · **事件流** —— 飞书收到的指令 / agent 发布的任务 / 执行过程 / 结果。
 *   · **计数** —— 五个 kind 的条数，点一下就是筛选。
 *
 * 🔴 风格统一（第 11 轮 · 2026-10-09 用户点名「这个板块的样式要和其他板块保持
 *    页面风格的基本统一性」）：外壳整个交给 `HomeSection` —— 标题 12px semibold、
 *    图标 h-3.5 灰、hint 11px 灰字、白底 p-3 全部复用 studio/ui 令牌。
 *    此前它自绘 `rounded-2xl + border + bg-surface-elevated`、标题 `text-base font-bold`，
 *    比左右邻居大一号、底色也不同 —— 这就是那处不统一。
 *    本文件从此**不写卡片底色/边框/圆角/标题字号**；要调风格改 home-primitives 一处。
 *
 * 🔴 默认 4 行 · 最近 20 条 · 一次最多 20 条 ·「更多」跳专项页（同日规格）：
 *    · 列表定高 `h-[13rem]` ≈ 4 行（行高 ≈ 52px = py-2.5 + 标题行 + 元信息行），
 *      卡被拉高时空隙沉在列表**下方**（卡内留白、读作呼吸感 —— 与运行状态卡同
 *      一条约定；第 12 轮起「更多」上移标题栏，卡底不再钉控件）；
 *      多于 4 行的内容靠列表内下拉（`overflow-y-auto`）。
 *    · `limit` 钳到 ≤ 20：卡片不做「加载更多」，要全量去专项页。
 *    ·「更多」在**标题栏右上**（2026-10-09 二次点名：先做在卡底，用户要求
 *      「移动到上方位置，美化按钮样式和排版」）。链接本身
 *      `<Link href={`/${locale}/agent`}>`：**不加** `withBasePath` ——
 *      `next/link` 会自动补 `next.config` 的 basePath，手工再加一次就成双前缀 404
 *      （welcome-header 注释与 quick-actions 走 resolveModuleHref 不带前缀，同一条坑；
 *       2026-10-09 本卡第一次就是这么写错的，SSR 里实测到双前缀后改回）。
 *
 * 刷新口径：开着每 6s 拉一次（关掉停轮询——关着就没有新事件，轮询只是白烧接口）；
 * 页面不可见（切标签/最小化）时本轮跳过。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import {
  Bug,
  ChevronDown,
  ChevronRight,
  Feather,
  Loader2,
  MessageSquare,
  Power,
  Workflow,
} from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { EmptyIcon, HomeEmpty, HomeError, HomeSection, HomeSpinner } from "@/components/home/home-primitives";
import { apiFetch } from "@/lib/api-fetch";

type Kind = "instruction" | "reply" | "task" | "step" | "result";
type Source = "feishu" | "portal" | "hermes";

interface AgentEventItem {
  id: string;
  kind: Kind;
  source: Source;
  actor: string;
  title: string;
  detail: string;
  status: "info" | "ok" | "fail";
  projectId: string;
  episodeId: string;
  runId: string;
  stageKey: string;
  createdAt: string;
}

interface ConsoleState {
  enabled: boolean;
  stats: { total: number; byKind: Record<Kind, number>; lastAt: string | null };
  events: AgentEventItem[];
}

const KINDS: Kind[] = ["instruction", "reply", "task", "step", "result"];

/** 🔴 卡片单次拉取上限（规格：最近 20 条 / 一次最多 20 条）。 */
const MAX_LIMIT = 20;

/** 列表定高 ≈ 4 行：li = py-2.5(20) + 标题 16 + mt-0.5 + 元信息 14 ≈ 52px。 */
const LIST_H = "h-[13rem]";

const KIND_STYLE: Record<Kind, string> = {
  instruction: "bg-sky-500/12 text-sky-600 dark:text-sky-400 border-sky-500/25",
  reply: "bg-teal-500/12 text-teal-600 dark:text-teal-400 border-teal-500/25",
  task: "bg-violet-500/12 text-violet-600 dark:text-violet-400 border-violet-500/25",
  step: "bg-slate-500/12 text-slate-600 dark:text-slate-400 border-slate-500/25",
  result: "bg-emerald-500/12 text-emerald-600 dark:text-emerald-400 border-emerald-500/25",
};

const KIND_ICON: Record<Kind, React.ReactNode> = {
  instruction: <Feather className="h-3 w-3" />,
  reply: <MessageSquare className="h-3 w-3" />,
  task: <Workflow className="h-3 w-3" />,
  step: <Bug className="h-3 w-3" />,
  result: <Power className="h-3 w-3" />,
};

function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const today = new Date();
  const sameDay =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  if (sameDay) return `${hh}:${mi}:${ss}`;
  return `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mi}`;
}

export function AgentConsoleCard({ limit = MAX_LIMIT }: { limit?: number } = {}) {
  const t = useTranslations("agentConsole");
  const locale = useLocale();
  const [state, setState] = useState<ConsoleState | null>(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState<Kind | "all">("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const enabledRef = useRef(true);
  // 🔴 一次最多 20 条（规格）：props 想传更多也钳住 —— 卡片不是全量入口。
  const take = Math.min(Math.max(1, limit), MAX_LIMIT);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await apiFetch(`/api/agent-console?limit=${take}`);
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        return;
      }
      const body = (await res.json()) as ConsoleState;
      setState(body);
      enabledRef.current = body.enabled;
      setError("");
    } catch {
      // 网络抖动不弹错误——下一轮轮询会自愈；只在首屏没数据时显示占位。
      setError((prev) => prev || "network");
    }
  }, [take]);

  useEffect(() => {
    void load();
  }, [load]);

  // 开着才轮询；页面不可见时跳过本轮（省接口、省电）。
  useEffect(() => {
    const timer = setInterval(() => {
      if (typeof document !== "undefined" && document.hidden) return;
      if (!enabledRef.current) return;
      void load();
    }, 6000);
    return () => clearInterval(timer);
  }, [load]);

  const toggle = useCallback(async () => {
    if (!state || busy) return;
    const next = !state.enabled;
    setBusy(true);
    // 乐观切换：开关手感要即时；失败则回滚并留在错误里。
    setState({ ...state, enabled: next });
    enabledRef.current = next;
    try {
      const res = await apiFetch("/api/agent-console", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = (await res.json()) as ConsoleState;
      setState(body);
      enabledRef.current = body.enabled;
      setError("");
    } catch {
      setState(state);
      enabledRef.current = state.enabled;
      setError("toggle");
    } finally {
      setBusy(false);
    }
  }, [state, busy]);

  const events = state?.events ?? [];
  const shown = useMemo(
    () => (filter === "all" ? events : events.filter((e) => e.kind === filter)),
    [events, filter]
  );
  const stats = state?.stats;

  return (
    // 容器只负责撑满格子 + 留数据钩子；**卡片长相全在 HomeSection 里**。
    <div
      className="flex min-h-0 flex-col"
      data-agent-console="1"
      data-agent-enabled={state ? String(state.enabled) : "loading"}
    >
      <HomeSection
        className="min-h-0 flex-1"
        title={
          <span className="flex items-center gap-1.5">
            <Workflow className="h-3.5 w-3.5 text-[var(--text-muted)]" aria-hidden />
            {t("title")}
          </span>
        }
        hint={
          <span className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className={`h-1.5 w-1.5 rounded-full ${
                state?.enabled === false ? "bg-[var(--text-muted)]" : "bg-emerald-500 animate-pulse"
              }`}
            />
            {state?.enabled === false ? t("subtitleOff") : t("subtitleOn")}
            {stats && stats.total > 0 ? (
              <span className="tabular-nums">
                · {t("lastAt", { time: stats.lastAt ? fmtTime(stats.lastAt) : "-" })}
              </span>
            ) : null}
          </span>
        }
        action={
          <>
            {/* 🔴 「更多」上移到标题栏右上（2026-10-09 二次点名）：原来是卡底
                footer（mt-auto 钉底），现与开关并排在标题行右侧。样式与开关同构
                —— rounded-full 细边小胶囊、同高同字号，hover 走主色 + 箭头右移半格
                （暗示「点了会跳走」）。 */}
            <Link
              href={`/${locale}/agent`}
              data-agent-more="1"
              className="group inline-flex shrink-0 items-center gap-0.5 rounded-full border border-[var(--border-subtle)] px-2 py-0.5 text-[11px] font-medium text-[var(--text-secondary)] transition hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
            >
              {t("more")}
              <ChevronRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </Link>
            {/* 开关：按钮语义（不是 checkbox），键盘可聚焦 */}
            <button
            type="button"
            role="switch"
            aria-checked={state?.enabled ?? true}
            aria-label={t("toggleAria")}
            disabled={!state || busy}
            onClick={toggle}
            data-agent-toggle="1"
            className={`group flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] transition disabled:opacity-60 ${
              state?.enabled === false
                ? "border-[var(--border-subtle)] bg-[var(--surface)] text-[var(--text-muted)]"
                : "border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
            }`}
          >
            <span className="relative inline-flex h-3.5 w-7 items-center">
              <span
                className={`absolute inset-0 rounded-full transition ${
                  state?.enabled === false ? "bg-[var(--border)]" : "bg-emerald-500"
                }`}
              />
              <span
                className={`relative z-10 h-3 w-3 rounded-full bg-white shadow transition-transform ${
                  state?.enabled === false ? "translate-x-0.5" : "translate-x-3.5"
                }`}
              />
            </span>
            {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            <span className="font-medium">
              {state?.enabled === false ? t("toggleOff") : t("toggleOn")}
            </span>
          </button>
          </>
        }
        footnote={
          error === "toggle" ? (
            <p className="text-[11px] text-[var(--destructive)]">{t("toggleFailed")}</p>
          ) : null
        }
        bodyClassName="flex min-h-0 flex-col gap-2"
      >
        {/* ── 计数条（点击筛选）────────────────────────────────── */}
        <div className="flex flex-wrap items-center gap-1.5">
          <FilterChip
            active={filter === "all"}
            onClick={() => setFilter("all")}
            label={t("all")}
            count={stats?.total ?? events.length}
          />
          {KINDS.map((k) => (
            <FilterChip
              key={k}
              active={filter === k}
              onClick={() => setFilter(filter === k ? "all" : k)}
              label={t(`kind_${k}` as const)}
              count={stats?.byKind?.[k] ?? 0}
              tone={KIND_STYLE[k]}
            />
          ))}
        </div>

        {/* ── 事件流：定高 4 行 + 卡内下拉 ─────────────────────── */}
        {/* 🔴 `h-[13rem]` 是**定高不是地板**（第 11 轮 · 2026-10-09）：规格要求
            默认只显示 4 行。上一版写 `min-h + flex-1` 是为了吃掉行内余量，
            代价是行被拉高时列表跟着长到 5-6 行 —— 那就不是「默认 4 行」。
            余量直接沉在列表下方（卡内留白，同运行状态卡的呼吸感约定；
            第 12 轮起卡底没有控件了，「更多」在标题栏右上）。 */}
        <div className={`${LIST_H} shrink-0 overflow-y-auto`}>
          {state === null && error ? (
            <HomeError message={t("pageLoadFailed")} onRetry={() => void load()} />
          ) : state === null ? (
            <p className="flex items-center gap-2 py-6 text-xs text-[var(--text-muted)]">
              <HomeSpinner className="h-3.5 w-3.5" /> {t("loading")}
            </p>
          ) : shown.length === 0 ? (
            <HomeEmpty
              compact
              icon={<EmptyIcon />}
              title={t("empty")}
              hint={state.enabled ? undefined : t("pausedHint")}
            />
          ) : (
            <ul className="divide-y divide-[var(--border-subtle)]">
              {shown.map((e) => {
                const open = openId === e.id;
                const kindStyle =
                  e.kind === "result" && e.status === "fail"
                    ? "bg-red-500/12 text-red-600 dark:text-red-400 border-red-500/25"
                    : e.kind === "result" && e.status === "ok"
                      ? KIND_STYLE.result
                      : KIND_STYLE[e.kind];
                return (
                  <li key={e.id} className="py-2.5" data-agent-event={e.kind}>
                    <div className="flex items-start gap-2">
                      <span className="mt-0.5 shrink-0 font-mono text-[10px] tabular-nums text-[var(--text-muted)]">
                        {fmtTime(e.createdAt)}
                      </span>
                      <span
                        className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-medium ${kindStyle}`}
                      >
                        {KIND_ICON[e.kind]}
                        {t(`kind_${e.kind}` as const)}
                      </span>
                      <div className="min-w-0 flex-1">
                        {/* 🔴 标题默认**只留一行**（2026-10-09 用户截图标注「保留一行」）：
                            飞书反馈类事件的 title 是一段长摘要，不截断时一条能折到 5 行。
                            全文走下方「详情」按钮（`<pre>` 展开，max-h-40 卡内滚动）。
                            用 line-clamp-1 而不是 truncate：span 是 flex 项（会被块化），
                            line-clamp 的 -webkit-box 能正常生效，超长串也不会顶破行。 */}
                        <p className="flex items-baseline gap-2 text-xs text-[var(--text-primary)]">
                          <span className="line-clamp-1 min-w-0 break-words">{e.title}</span>
                          <span className="shrink-0 text-[10px] text-[var(--text-muted)]">
                            {t(`source_${e.source}` as const)}
                          </span>
                        </p>
                        <p className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[10px] text-[var(--text-muted)]">
                          {e.projectId ? (
                            <span>{t("refProject", { id: e.projectId.slice(0, 8) })}</span>
                          ) : null}
                          {e.episodeId ? (
                            <span>{t("refEpisode", { id: e.episodeId.slice(0, 8) })}</span>
                          ) : null}
                          {e.stageKey ? <span className="font-mono">{e.stageKey}</span> : null}
                          {e.actor ? (
                            <span>{t("refActor", { actor: e.actor.slice(0, 16) })}</span>
                          ) : null}
                          {e.detail ? (
                            <button
                              type="button"
                              onClick={() => setOpenId(open ? null : e.id)}
                              data-agent-event-detail={e.id}
                              className="inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[10px] text-primary hover:bg-primary/10"
                            >
                              {open ? t("hideDetail") : t("detail")}
                              <ChevronDown
                                className={`h-3 w-3 transition ${open ? "rotate-180" : ""}`}
                              />
                            </button>
                          ) : null}
                        </p>
                        {open && e.detail ? (
                          <pre className="mt-1.5 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-[var(--surface)] px-2.5 py-2 text-[11px] leading-relaxed text-[var(--text-secondary)]">
                            {e.detail}
                          </pre>
                        ) : null}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

      </HomeSection>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  count,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-agent-chip={label}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition ${
        active
          ? tone
            ? `${tone} font-medium`
            : "border-primary/30 bg-primary/10 font-medium text-primary"
          : "border-[var(--border-subtle)] text-[var(--text-muted)] hover:bg-[var(--surface)]"
      }`}
    >
      {label}
      <span className="font-mono tabular-nums opacity-70">{count}</span>
    </button>
  );
}
